import * as yup from 'yup';
import { DateTime } from 'luxon';

enum MinorPickUp {
  Itself = 1,
  Parent = 2,
  Guardian = 3,
}

const today = DateTime.now();

export const DEFAULT_MAX_STRING_MSG = 'Provided value is too long';
export const EMAIL_REGEX = new RegExp(
  '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$',
  'gi',
);
export const NAME_REGEX = new RegExp("^(?=(?:[^ ]* ){0,3}[^ ]*$)(?![ '\\-.,/])(?:[\\p{L}\\p{M}]['\\-.,/]? ?)*[\\p{L}\\p{M}]$", "u");
export const LAST_NAME_REGEX = new RegExp(
  "^(?=(?:[^ ]* ){0,3}[^ ]*$)(?:-$|(?!(?:[ '\\-.,/]))(?:[\\p{L}\\p{M}](?:['\\-.,/]? ?)?)*[\\p{L}\\p{M}])$", "u");
export const STRING_DEFAULT_MAX_LENGTH = 100;

export const VALIDATORS = {
  firstName: yup
    .string()
    .required('Given name is required.')
    .matches(NAME_REGEX, 'Please enter valid given name')
    .max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
  lastName: yup
    .string()
    .required('Surname is required.')
    .matches(LAST_NAME_REGEX, 'Please enter valid surname')
    .max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
  email: yup
    .string()
    .optional()
    .matches(EMAIL_REGEX, 'Please specify a valid email address')
    .email('Please specify a valid email address')
    .max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
  emailConfirm: yup
    .string()
    .when('email', {
      is: (value: string) => !value || value.length === 0,
      then: yup.string().optional(),
      otherwise: yup
        .string()
        .required('Confirm email must match email')
        .max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG)
        .oneOf([yup.ref('email'), null], 'Confirm email must match email'),
    })
    .oneOf([yup.ref('email'), null], 'Confirm email must match email'),
  dateOfBirthRange: yup
    .date()
    .required('Date of birth is required to create an account')
    .min(today.minus({ year: 100 }).toISODate(), 'Customer cannot be older than 100 years old')
    .max(today.toISODate(), 'Please chose date in past'),
  dateOfBirthAdult: (message = 'Customer must be over 18 years old') =>
    yup
      .date()
      .required('Date of birth is required')
      .max(today.minus({ year: 18 }).toISODate(), message),
  dateOfBirthMinor: yup
    .date()
    .required('Date of birth is required')
    .min(today.minus({ year: 18 }).toISODate(), 'Minor must be below 18 years old'),
  gender: yup.number().required().typeError('Customer gender is required').min(0, 'Customer gender is required').required('Customer gender is required'),
  address: yup.object({
    residenceCountryId: yup
      .number()
      .typeError('Customer country of residence is required')
      .min(0, 'Customer country of residence is required')
      .required('Customer country of residence is required'),
    addressLine1: yup
      .string()
      .typeError('Customer address is required')
      .required('Customer address is required')
      .max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
    city: yup.string().typeError('Customer city is required').required('Customer city is required').max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
    postCode: yup.string().max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
  }),
  minorPickUp: yup
    .number()
    .nullable(true)
    .min(1, "It's required to select who will pick up minor")
    .typeError("It's required to select who will pick up minor")
    .required("It's required to select who will pick up minor"),
  guardians: yup.array().when('minorPickUp', {
    is: (value: number) => value === MinorPickUp.Guardian,
    then: yup
      .array()
      .of(
        yup.object().shape({
          fullName: yup.string().required('Guardian name required').max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
          telephone: yup.string().required('Guardian telephone required').max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
          relationshipId: yup.string().required('Guardian relationship required'),
          ageDeclaration: yup.boolean().required('Guardian age declaration required').oneOf([true], 'Guardian age declaration required'),
        }),
      )
      .min(1, 'Guardian is required'),
    otherwise: yup.array().optional(),
  }),
};

export const isAdult = (date: string = ''): boolean => {
  return DateTime.fromISO(date).toMillis() < today.minus({ year: 18 }).toMillis();
};
